import type { Counterparty } from '@numistic/contracts'
import {
  useCounterparties,
  useCreateCounterparty,
  useMeta,
  useRemoveCounterparty,
  useUpdateCounterparty,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
  Textarea,
} from '@numistic/ui'
import { createRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { MutationErrors } from '../../components/mutation-errors.js'
import { formatDate, formatMoney } from '../../lib/format.js'
import { settingsRoute } from './layout.js'

export const settingsCounterpartiesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'counterparties',
  component: CounterpartiesPage,
})

/**
 * Справочник продавцов и площадок: магазины, аукционы, дилеры. Пополняется сам из поля
 * «Где / у кого» при покупке; здесь — вид, сайт, заметка, переименование и итоги по сделкам.
 */
function CounterpartiesPage() {
  const { t } = useTranslation()
  const list = useCounterparties()
  const create = useCreateCounterparty()
  const update = useUpdateCounterparty()
  const remove = useRemoveCounterparty()
  const [name, setName] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const rows = list.data ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('counterparties.title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('counterparties.hint')}</p>
      </CardHeader>
      <CardContent className="grid gap-4">
        {list.isPending ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : rows.length ? (
          <ul className="divide-y">
            {rows.map((c) => (
              <CounterpartyRow
                key={`${c.id}:${c.name}:${c.kind}:${c.url}:${c.note}`}
                item={c}
                open={open === c.id}
                pending={update.isPending || remove.isPending}
                onToggle={() => setOpen((o) => (o === c.id ? null : c.id))}
                onSave={(patch) =>
                  update.mutate({ id: c.id, ...patch }, { onSuccess: () => setOpen(null) })
                }
                onRemove={() => {
                  if (window.confirm(t('counterparties.deleteConfirm', { name: c.name })))
                    remove.mutate(c.id)
                }}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('counterparties.empty')}</p>
        )}
        <form
          className="flex gap-2 border-t pt-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) create.mutate({ name: name.trim() }, { onSuccess: () => setName('') })
          }}
        >
          <Input
            aria-label={t('counterparties.new')}
            placeholder={t('counterparties.new')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="outline" disabled={create.isPending || !name.trim()}>
            {t('common.add')}
          </Button>
        </form>
        <MutationErrors errors={[create.error, update.error, remove.error]} />
      </CardContent>
    </Card>
  )
}

function CounterpartyRow({
  item,
  open,
  pending,
  onToggle,
  onSave,
  onRemove,
}: {
  item: Counterparty
  open: boolean
  pending: boolean
  onToggle: () => void
  onSave: (patch: {
    name?: string
    kind?: string | null
    url?: string | null
    note?: string | null
  }) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const meta = useMeta().data
  const kinds = meta?.enums.acquisition_source ?? []
  const [name, setName] = useState(item.name)
  const [kind, setKind] = useState(item.kind ?? '')
  const [url, setUrl] = useState(item.url ?? '')
  const [note, setNote] = useState(item.note ?? '')
  const kindName = kinds.find((k) => k.code === item.kind)?.name.ru
  const totals = [
    t('counterparties.deals', { count: item.events }),
    Number(item.spent)
      ? `${t('counterparties.spent')} ${formatMoney(item.spent, item.baseCurrency)}`
      : null,
    Number(item.earned)
      ? `${t('counterparties.earned')} ${formatMoney(item.earned, item.baseCurrency)}`
      : null,
    item.lastOn
      ? `${t('counterparties.last')} ${formatDate(new Date(`${item.lastOn}T00:00:00`))}`
      : null,
  ].filter(Boolean)

  return (
    <li className="grid gap-3 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="mr-auto grid min-w-0 text-left"
        >
          <span className="truncate text-sm font-medium hover:text-primary">{item.name}</span>
          <span className="text-xs text-muted-foreground">
            {[kindName, ...totals].filter(Boolean).join(' · ')}
          </span>
        </button>
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-primary underline underline-offset-4"
          >
            {t('counterparties.site')}
          </a>
        ) : null}
        <Button type="button" variant="ghost" size="sm" onClick={onToggle} aria-expanded={open}>
          {open ? t('common.cancel') : t('common.edit')}
        </Button>
      </div>
      {open ? (
        <form
          className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (!name.trim()) return
            onSave({
              name: name.trim(),
              kind: kind || null,
              url: url.trim() || null,
              note: note.trim() || null,
            })
          }}
        >
          <Field label={t('counterparties.name')} hint={t('counterparties.renameHint')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label={t('counterparties.kind')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t('common.notSet')}</option>
              {kinds.map((k) => (
                <option key={k.code} value={k.code}>
                  {k.name.ru}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('counterparties.url')} className="sm:col-span-2">
            <Input
              type="url"
              placeholder="https://"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </Field>
          <Field label={t('counterparties.note')} className="sm:col-span-2">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button type="submit" disabled={pending || !name.trim()}>
              {t('common.save')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="ml-auto text-destructive"
              disabled={pending}
              onClick={onRemove}
            >
              {t('common.remove')}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  )
}
